import { useMarket } from "@app/_context";
import signInWithEmail from "../services/signInWithEmail";
import signInWithProvider from "../services/signInWithProvider";
import useAuth from "./useAuth";
import useSignOut from "./useSignOut";

export type SignInStrategy = "email" | "google" | "facebook";

export type SignInProps =
  | {
      strategy: "email";
      email: string;
      password: string;
    }
  | {
      strategy: "google" | "facebook";
      email?: never;
      password?: never;
    };

export default function useSignIn() {
  const signOut = useSignOut();
  const { market } = useMarket().state;
  const { auth, authenticateUser } = useAuth();

  return async function signIn({ strategy, email, password }: SignInProps) {
    if (!auth) {
      throw new Error("Auth is not initialized");
    }

    try {
      let user;
      switch (strategy) {
        case "email": {
          user = await signInWithEmail({
            auth,
            email,
            password,
            market,
          });
          break;
        }
        case "google": {
          const { GoogleAuthProvider } = await import("firebase/auth");
          user = await signInWithProvider({
            auth,
            provider: new GoogleAuthProvider(),
            market,
          });
          break;
        }
        case "facebook": {
          const { FacebookAuthProvider } = await import("firebase/auth");
          user = await signInWithProvider({
            auth,
            provider: new FacebookAuthProvider(),
            market,
          });
          break;
        }
        default:
          return;
      }

      // Wait until context has user/token (deduped with onIdTokenChanged).
      await authenticateUser(user);
    } catch (error) {
      await signOut({ redirectTo: false });
      throw error;
    }
  };
}
